import { useCallback, useMemo, useRef } from 'react';
import { useGetResults } from 'hooks/getResults';
import { useResultsState } from 'hooks/useResultsState';
import type { ExportStatusValue } from 'types/result';
import type { Ordering } from 'screens/home/utils';

interface UsePaginatedResultsParams {
  ordering?: Ordering | string;
  start_date?: string;
  end_date?: string;
  search?: string;
  export_status?: ExportStatusValue | undefined;
  medatixxPatientId?: string;
  page_size?: number;
}

function findScrollParent(element: Element | null): Element | null {
  let current = element?.parentElement ?? null;
  while (current && current !== document.body) {
    const { overflowY } = getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') return current;
    current = current.parentElement;
  }
  return null;
}

export function usePaginatedResults(
  params?: UsePaginatedResultsParams,
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetResults(params, options);

  const allResults = useMemo(() => {
    const seen = new Set<number>();
    return (data?.pages.flatMap((page) => page.results) ?? []).filter((result) => {
      if (seen.has(result.id)) return false;
      seen.add(result.id);
      return true;
    });
  }, [data?.pages]);
  const { resultsState, handleStatusUpdate } = useResultsState(allResults);

  const observerRef = useRef<IntersectionObserver | null>(null);

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node || !hasNextPage || isError) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !isFetchingNextPage) void fetchNextPage();
        },
        { root: findScrollParent(node), rootMargin: '200px' }
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [hasNextPage, isError, isFetchingNextPage, fetchNextPage]
  );

  return {
    data,
    isLoading,
    isError,
    error,
    hasNextPage,
    isFetchingNextPage,
    resultsState,
    handleStatusUpdate,
    sentinelRef,
    fetchNextPage,
  };
}
